import { createContext, useState, useContext } from "react";

export const ForeignShopRuleContext = createContext();

/** @typedef {Object} PriceTrackingHelper
 * @property {(active: boolean) => void} setActive
 * @property {() => boolean} isActive
 * @property {(val: boolean) => void} setExcludeShops
 * @property {() => boolean} getExcludeShops
 * @property {(val: boolean) => void} setUseAllProviders
 * @property {() => boolean} getUseAllProviders
 * @property {(val: Object) => void} setShopKeysWithPrio
 * @property {() => Object} getShopKeysWithPrio
 * @property {(val: {filter_id: number, filter_values: string}) => void} setProviderFilter
 * @property {() => {filter_id: number, filter_values: string}} getProviderFilter
 * @property {() => boolean} getExcludeShopIfDeliveryCostAreNotAvailable
 * @property {(val: bool) => void} setExcludeShopIfDeliveryCostAreNotAvailable 
 * @property {() => boolean} isConfigured
 */

/** @typedef {Object} ForeignShopStockHelper
 * @property {() => Object[]} getForeignShopStockRules
 * @property {(stockRule: Object) => string} addForeignShopStockRule
 * @property {(ruleId: string) => void} removeForeignShopStockRule
 * @property {(ruleId: string, field: string, value: any) => void} updateForeignShopStockRule
 * @property {(ruleId: string) => Object|null} getForeignShopStockRule
 * @property {() => boolean} hasActiveForeignShopStockRules
 * @property {() => boolean} hasConfiguredForeignShopStockRules
 */

/**@typedef {Object} ForeignShopRuleHelper
 * @property {() => Object[]} getActiveRules
 */

/** @typedef {Object} ForeignShopRuleContextValue
 * @property {PriceTrackingHelper} PriceTrackingHelper
 * @property {ForeignShopStockHelper} ForeignShopStockHelper
 * @property {ForeignShopRuleHelper} ForeignShopRuleHelper
 */

export const ForeignShopRuleContextProvider = ({ children }) => {
     const [foreignShopRuleData, setForeignShopRuleData] = useState({
          shops: {
               active: false,
               keyString: "priceTracking",
               excludeShopIfDeliveryCostAreNotAvailable: false,
               useAllProviders: true, 
               shopKeysWithPrio: {}, 
               providerFilter: { 
                    filter_id: -1,
                    filter_values: ""
               }
          },
          useAllPricesFromGroup: {
               active: false,
               keyString: "useAllPricesFromGroup",
               name: "Preise von Produktgruppe einbeziehen",
               groupName: ""
          },
          foreignShopStockRules: []
     });

     const updateField = (section, field, value) => {
          setForeignShopRuleData((prev) => ({
               ...prev,
               [section]: {
                    ...prev[section],
                    [field]: value
               }
          }));
     };

     const generateForeignShopStockRuleId = () => `foreignshop_stock_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

     const addForeignShopStockRule = (stockRule = {}) => {
          const ruleId = generateForeignShopStockRuleId();
          const newStockRule = {
               id: ruleId,
               keyString: "foreignShopstock",
               name: "Fremdshop Verfügbarkeit",
               active: true,
               stock: [],
               border_style: "",
               foreignshop_stock: [],
               stockRuleReference: null,
               ...stockRule
          };

          setForeignShopRuleData(prev => ({
               ...prev,
               foreignShopStockRules: [...prev.foreignShopStockRules, newStockRule].sort((left, right) => {
                    if (!left.stockRuleReference) return 1;
                    if (!right.stockRuleReference) return -1;
                    
                    const leftId = typeof left.stockRuleReference === 'object' && left.stockRuleReference.id 
                         ? left.stockRuleReference.id 
                         : left.stockRuleReference;
                    const rightId = typeof right.stockRuleReference === 'object' && right.stockRuleReference.id 
                         ? right.stockRuleReference.id 
                         : right.stockRuleReference;
                    
                    const leftTime = parseInt(leftId.split("_")[1], 10);
                    const rightTime = parseInt(rightId.split("_")[1], 10);
                    return leftTime - rightTime;
               })
          }));

          return ruleId;
     };

     const removeForeignShopStockRule = (ruleId) => {
          setForeignShopRuleData(prev => ({
               ...prev,
               foreignShopStockRules: prev.foreignShopStockRules.filter(rule => rule.id !== ruleId)
          }));
     };

     const updateForeignShopStockRule = (ruleId, fieldOrObject, value) => {
          setForeignShopRuleData(prev => ({
               ...prev,
               foreignShopStockRules: prev.foreignShopStockRules.map(rule => {
                    if (rule.id !== ruleId) return rule;
                    if (typeof fieldOrObject === 'object' && fieldOrObject !== null) {
                         return { ...rule, ...fieldOrObject };
                    }
                    return { ...rule, [fieldOrObject]: value };
               })
          }));
     };

     const getForeignShopStockRule = (ruleId) => {
          return foreignShopRuleData.foreignShopStockRules.find(rule => rule.id === ruleId) || null;
     };

     const isForeignShopStockRuleConfigured = (rule) => {
          if (!rule) return false;
          return Boolean(rule.stockRuleReference) &&
               Array.isArray(rule.stock) &&
               rule.stock.some(Boolean);
     };

     return (
          <ForeignShopRuleContext.Provider value={{
               PriceTrackingHelper: {
                    setActive: (val) => {
                         if (val && typeof window !== 'undefined' && window.checkPriceChangeMarginActive?.()) {
                              alert("Preistracking Mitbewerb kann nicht aktiviert werden, da bereits eine Preisanpassung nach Marge Komponente existiert.");
                              return;
                         }
                         updateField("shops", "active", val);
                    },
                    isActive: () => foreignShopRuleData.shops.active,
                    
                    togglePriceTracking: (active) => updateField("shops", "active", active),
                    setExcludeShops: (val) => updateField("shops", "excludeShopIfDeliveryCostAreNotAvailable", val),
                    getExcludeShops: () => foreignShopRuleData.shops.excludeShopIfDeliveryCostAreNotAvailable,

                    setUseAllProviders: (val) => updateField("shops", "useAllProviders", val),
                    getUseAllProviders: () => foreignShopRuleData.shops.useAllProviders,

                    setShopKeysWithPrio: (val) => updateField("shops", "shopKeysWithPrio", val),
                    getShopKeysWithPrio: () => foreignShopRuleData.shops.shopKeysWithPrio,

                    setProviderFilter: (val) => updateField("shops", "providerFilter", val),
                    getProviderFilter: () => foreignShopRuleData.shops.providerFilter,
               
                    getExcludeShopIfDeliveryCostAreNotAvailable: () => foreignShopRuleData.shops.excludeShopIfDeliveryCostAreNotAvailable,
                    setExcludeShopIfDeliveryCostAreNotAvailable: (val) => updateField("shops", "excludeShopIfDeliveryCostAreNotAvailable", val),
                    isConfigured: () => {
                         const data = foreignShopRuleData.shops;
                         const hasShops =
                              data.shopKeysWithPrio && Object.keys(data.shopKeysWithPrio).length > 0;
                         const hasProviderFilter = data.providerFilter && data.providerFilter.filter_id !== -1;
                         return hasShops || hasProviderFilter;
                    }
               },

               ForeignShopStockHelper: {
                    getForeignShopStockRules: () => foreignShopRuleData.foreignShopStockRules,
                    addForeignShopStockRule,
                    removeForeignShopStockRule,
                    updateForeignShopStockRule,
                    getForeignShopStockRule,
                    hasActiveForeignShopStockRules: () => foreignShopRuleData.foreignShopStockRules.length > 0,
                    hasConfiguredForeignShopStockRules: () =>
                         foreignShopRuleData.foreignShopStockRules.some((rule) => isForeignShopStockRuleConfigured(rule)),
               },

               UseAllPricesFromGroupHelper: {
                    setActive: (val) => updateField("useAllPricesFromGroup", "active", val),
                    isActive: () => foreignShopRuleData.useAllPricesFromGroup.active,
                    setGroupName: (val) => updateField("useAllPricesFromGroup", "groupName", val),
                    getGroupName: () => foreignShopRuleData.useAllPricesFromGroup.groupName,
                    isConfigured: () => !!foreignShopRuleData.useAllPricesFromGroup.groupName,
               },

               ForeignShopRuleHelper: {
                    getActiveRules: () => [
                         ...Object.values(foreignShopRuleData).filter((data) => 
                              typeof data === 'object' && 
                              data.active && 
                              data.keyString !== undefined &&
                              !Array.isArray(data)
                         ),
                         ...foreignShopRuleData.foreignShopStockRules.filter(
                              (rule) => rule.active && isForeignShopStockRuleConfigured(rule)
                         )
                    ]
               }
          }}>
               {children}
          </ForeignShopRuleContext.Provider>
     );
};

/** @returns {ForeignShopRuleContextValue} */
export const useForeignShopRule = () => {
     const context = useContext(ForeignShopRuleContext);
     if (!context) {
          throw new Error("useForeignShopRule muss innerhalb des ForeignShopRuleContextProviders verwendet werden");
     }
     return context;
};